import { useEffect, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProjectImageSettingsSection } from "@/components/image-generation/project-image-settings-section";
import { ProjectForm } from "@/components/projects/project-form";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ProjectImageSettingsDraft } from "@/features/image-generation";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import {
  formatProjectErrorMessage,
  type ProjectCardView,
  type UpdateProjectInput,
} from "@/features/projects";
import { useProjectShellStore } from "@/stores/projectShellStore";

type ProjectSettingsDialogProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  project: ProjectCardView | null;
};

export function ProjectSettingsDialog({
  onOpenChange,
  open,
  project,
}: ProjectSettingsDialogProps) {
  const { t } = useTranslation();
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);
  const [imageDraft, setImageDraft] = useState<ProjectImageSettingsDraft>({
    styleDescription: "",
  });
  const hydratedDraftProjectIdRef = useRef<string | null>(null);
  const [imageSubmitError, setImageSubmitError] = useState<string | null>(null);
  const busy = useProjectShellStore((state) => state.settingsBusy);
  const addProjectArtstyleReference = useProjectShellStore(
    (state) => state.addProjectArtstyleReference,
  );
  const clearProjectImageSettings = useProjectShellStore(
    (state) => state.clearProjectImageSettings,
  );
  const hydrateProjectImageSettings = useProjectShellStore(
    (state) => state.hydrateProjectImageSettings,
  );
  const imageSettingsStatus = useProjectShellStore((state) => state.imageSettingsStatus);
  const projectImageSettings = useProjectShellStore((state) => state.projectImageSettings);
  const removeProjectArtstyleReference = useProjectShellStore(
    (state) => state.removeProjectArtstyleReference,
  );
  const reorderProjectArtstyleReferences = useProjectShellStore(
    (state) => state.reorderProjectArtstyleReferences,
  );
  const replaceProjectArtstyleReference = useProjectShellStore(
    (state) => state.replaceProjectArtstyleReference,
  );
  const updateProject = useProjectShellStore((state) => state.updateProject);
  const updateProjectArtDirection = useProjectShellStore(
    (state) => state.updateProjectArtDirection,
  );
  const imageCreditCost = useRuntimeCreditCost("image_generation");
  const projectId = project?.projectId ?? null;

  useEffect(() => {
    if (!open || !projectId) {
      setImageDraft({ styleDescription: "" });
      hydratedDraftProjectIdRef.current = null;
      setImageSubmitError(null);
      clearProjectImageSettings();
      return;
    }

    setImageSubmitError(null);
    void hydrateProjectImageSettings(projectId);
  }, [
    clearProjectImageSettings,
    hydrateProjectImageSettings,
    open,
    projectId,
  ]);

  useEffect(() => {
    if (
      !projectImageSettings
      || !projectId
      || projectImageSettings.projectId !== projectId
      || hydratedDraftProjectIdRef.current === projectId
    ) {
      return;
    }

    setImageDraft({
      styleDescription: projectImageSettings.styleDescription,
    });
    hydratedDraftProjectIdRef.current = projectId;
  }, [projectId, projectImageSettings]);

  if (!project || !projectId) {
    return null;
  }

  const currentProject = project;

  async function handleSubmit(input: UpdateProjectInput) {
    await updateProject(currentProject.projectId, input);
    onOpenChange(false);
  }

  async function handleSaveArtDirection() {
    setImageSubmitError(null);

    try {
      await updateProjectArtDirection(currentProject.projectId, imageDraft);
    } catch (error) {
      setImageSubmitError(formatProjectErrorMessage(error));
    }
  }

  async function handleAddReference(file: File) {
    setImageSubmitError(null);

    try {
      await addProjectArtstyleReference(currentProject.projectId, file);
    } catch (error) {
      setImageSubmitError(formatProjectErrorMessage(error));
    }
  }

  async function handleRemoveReference(referencePath: string) {
    setImageSubmitError(null);

    try {
      await removeProjectArtstyleReference(currentProject.projectId, referencePath);
    } catch (error) {
      setImageSubmitError(formatProjectErrorMessage(error));
    }
  }

  async function handleReplaceReference(referencePath: string, file: File) {
    setImageSubmitError(null);

    try {
      await replaceProjectArtstyleReference(currentProject.projectId, referencePath, file);
    } catch (error) {
      setImageSubmitError(formatProjectErrorMessage(error));
    }
  }

  async function handleReorderReferences(orderedReferencePaths: string[]) {
    setImageSubmitError(null);

    try {
      await reorderProjectArtstyleReferences(currentProject.projectId, orderedReferencePaths);
    } catch (error) {
      setImageSubmitError(formatProjectErrorMessage(error));
    }
  }

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!busy) {
          onOpenChange(nextOpen);
        }
      }}
      open={open}
    >
      <DialogContent className="w-[min(96vw,76rem)] max-h-[90vh] gap-0 overflow-hidden border-[#eadcc7] bg-[#f5f1e8] p-0 shadow-[0_28px_90px_-36px_rgba(56,40,22,0.28)] backdrop-blur-none">
        <DialogHeader className="border-b border-ink/10 px-8 pb-6 pt-8 pr-16">
          <DialogTitle>{t("projects.actions.settings")}</DialogTitle>
          <DialogDescription className="max-w-3xl text-base leading-7">
            {t("projects.dialogs.settingsBody")}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto">
          {busy ? (
            <div className="border-b border-ink/10 bg-[#efe7da] px-8 py-3 text-sm text-secondary-foreground">
              <div className="flex items-center gap-3">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t("projects.dialogs.saving")}
              </div>
            </div>
          ) : null}

          <div className="grid gap-10 px-8 py-8 xl:grid-cols-[minmax(0,0.84fr)_minmax(0,1.16fr)] xl:gap-8">
            <section className="space-y-8 xl:border-r xl:border-ink/10 xl:pr-8">
              <ProjectForm
                busy={busy}
                initialCoverImageUrl={currentProject.coverImageUrl}
                initialGenre={currentProject.genre}
                initialTitle={currentProject.title}
                layout="settings"
                onCoverPreviewClick={() => setCoverLightboxOpen(true)}
                onSubmit={handleSubmit}
                submitLabel={t("projects.form.save")}
              />
            </section>

            <ProjectImageSettingsSection
              busy={busy}
              creditCost={imageCreditCost}
              draft={imageDraft}
              onAddReference={handleAddReference}
              onDraftChange={(nextDraft) =>
                setImageDraft((current) => ({
                  ...current,
                  ...nextDraft,
                }))
              }
              onRemoveReference={handleRemoveReference}
              onReorderReferences={handleReorderReferences}
              onReplaceReference={handleReplaceReference}
              onSave={handleSaveArtDirection}
              savedSettings={
                projectImageSettings?.projectId === currentProject.projectId
                  ? projectImageSettings
                  : null
              }
              status={imageSettingsStatus}
              submitError={imageSubmitError}
              variant="sheet"
            />
          </div>
        </div>
      </DialogContent>

      <ImageLightbox
        alt={`${currentProject.title} cover`}
        imageUrl={currentProject.coverImageUrl}
        onOpenChange={setCoverLightboxOpen}
        open={coverLightboxOpen}
        title={currentProject.title}
      />
    </Dialog>
  );
}
